import type { ReactNode } from "react";
import { Widget, WidgetSkeleton } from "@nexus/widget-sdk";

import { SectionLayout } from "@/components/layout/SectionLayout";
import { WORKSPACE_LABEL } from "@/config/uiConfig";
import { useDashboardPlacement } from "./DashboardLayout";

/** Where a dashboard will appear, before its record is known: KPI strip, then two charts. */
function DashboardPlaceholder() {
  return (
    <div className="density-dashboard-grid grid grid-cols-12" aria-busy="true">
      <div className="col-span-12">
        <Widget hideHeader>
          <WidgetSkeleton shape="kpis" label="Loading dashboard" />
        </Widget>
      </div>
      {[0, 1].map((index) => (
        <div key={index} className="col-span-12 min-h-72 lg:col-span-6">
          <Widget hideHeader>
            <WidgetSkeleton shape="chart" label="Loading dashboard" />
          </Widget>
        </div>
      ))}
    </div>
  );
}

/**
 * The dashboard page shell while its record loads, or with a page-level state
 * (error, empty) in place of the dashboard. Unknown loading titles use a skeleton.
 */
export function DashboardRouteState({ title, children }: { title?: string; children?: ReactNode }) {
  const { section, sectionTitle, sidebarItems } = useDashboardPlacement();
  const loading = children === undefined;
  const pageTitle = title || (loading ? "Loading dashboard" : "Dashboard");
  return (
    <SectionLayout
      sectionTitle={sectionTitle}
      sidebarItems={sidebarItems}
      title={pageTitle}
      titleLoading={loading && !title}
      breadcrumbTrail={
        section === "workspace"
          ? [
              { label: WORKSPACE_LABEL, href: "/workspace/dashboards" },
              { label: "Dashboards", href: "/workspace/dashboards" },
              { label: pageTitle },
            ]
          : undefined
      }
      variant="dashboard"
    >
      {children ?? <DashboardPlaceholder />}
    </SectionLayout>
  );
}
